import {
  QueryClient,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { sortObjectKeys } from '@vindr/utils/helpers/common.helper';
import {
  createElement,
  deleteElement,
  downloadImageElement,
  getElement,
  getElementDetail,
  getImageUrl,
  getURLUploadImageElement,
  updateElement,
} from '../apis/element.api';
import { queryKey } from '../constants/queryClientKey';

export function useGetElement(
  param: Parameters<typeof getElement>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.element, sortObjectKeys(param)],
    () => getElement(param),
    {
      keepPreviousData: true,
      ...options,
      onError: (err) => {
        console.error('useGetElement', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getElement>>>;
}

async function fetchLinkedElements(
  element: Awaited<ReturnType<typeof getElementDetail>>['data']['elements'][0],
  queryClient: QueryClient,
  visited: Set<string> = new Set()
) {
  // No linked elements, return as is
  if (!element?.linked_element_ids) return element;

  // If element has already been visited, avoid fetching it again
  if (visited.has(element.id)) return element;

  visited.add(element.id);

  const linkedElements = await Promise.all(
    element.linked_element_ids.map(async (linkedId) => {
      const linkedCacheKey = [queryKey.element_detail, { id: linkedId }];

      // Check if the linked element is already in the cache
      let linkedElement = queryClient.getQueryData(linkedCacheKey);

      if (!linkedElement) {
        // Fetch the linked element if not in cache
        const response = await getElementDetail({ id: linkedId });
        linkedElement = response?.data;

        // Add the fetched linked element to the cache
        queryClient.setQueryData(linkedCacheKey, linkedElement);
      }

      // Recursively fetch any linked elements inside this linked element
      return fetchLinkedElements(linkedElement, queryClient, visited);
    })
  );

  return {
    ...element,
    elements: linkedElements,
  };
}

export async function getElementDetailAsync(
  param: Parameters<typeof getElementDetail>[0],
  queryClient: QueryClient
): Promise<Awaited<ReturnType<typeof getElementDetail>>['data'] | undefined> {
  // Clean the parameter as in the hook
  const cleanedParam = {
    ...param,
    ...(param.id && { id: param.id.replace(/\|.*/, '') }),
  };

  // First fetch the main element details
  const elementDetail = await getElementDetail(cleanedParam);
  const elementDetailData = elementDetail?.data;

  if (!elementDetailData) {
    throw new Error('Element detail data is undefined');
  }

  const visited = new Set<string>();

  // Recursively fetch linked elements and add to cache
  const elements = await Promise.all(
    elementDetailData.elements.map((e) =>
      fetchLinkedElements(e, queryClient, visited)
    )
  );

  return {
    ...elementDetailData,
    elements,
  };
}

export function useGetElementDetail(
  param: Parameters<typeof getElementDetail>[0],
  options?: UseQueryOptions
) {
  const queryClient = useQueryClient();
  const cacheKey = [queryKey.element_detail, sortObjectKeys(param)];

  return useQuery(cacheKey, () => getElementDetailAsync(param, queryClient), {
    keepPreviousData: true,
    staleTime: Infinity,
    cacheTime: Infinity,
    ...options,
    onError: (err) => {
      console.error('useGetElementDetail error', err);
    },
  }) as UseQueryResult<Awaited<ReturnType<typeof getElementDetail>>['data']>;
}

/*
export const useGetElementInfinity = (
  params: Parameters<typeof getElement>[0],
  options?: UseInfiniteQueryOptions
) => {
  const queryInfo = useInfiniteQuery(
    [queryKey.share_infinite, sortObjectKeys(params)],
    (e) =>
      getElement({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: (lastPage: any, allPages) => {
        if (lastPage.data.data.length < params.limit) return undefined;
        return allPages.reduce((acc, page) => acc + page.data?.data.length, 0);
      },
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getElement>>>;

  const data = queryInfo.data?.pages.map((page) => page.data.data || []).flat();

  return {
    ...queryInfo,
    data,
    count: queryInfo.data?.pages[0].count,
  };
}; */

export function useCreateElement() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: Parameters<typeof createElement>[0]) =>
      createElement(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.element]);
      },
      onError: (err) => {
        console.error('useCreateElement', err);
      },
    }
  );
}

export function useUpdateElement() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      id,
      data,
    }: {
      id: Parameters<typeof updateElement>[0];
      data: Parameters<typeof updateElement>[1];
    }) => updateElement(id, data),
    {
      onSuccess: (_, params) => {
        queryClient.invalidateQueries([queryKey.element], { exact: false });
        const cacheKey = [queryKey.element_detail, { id: params?.id }];

        queryClient.invalidateQueries(cacheKey, { exact: false });

        const existingData = queryClient.getQueryData(cacheKey);

        if (existingData) {
          // If data exists, remove the cache entry for 'element_detail'
          queryClient.removeQueries(cacheKey, { exact: false });
        }
      },

      onError: (err) => {
        console.error('useUpdateElement', err);
      },
    }
  );
}

export function useDeleteElement() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteElement(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.element], { exact: false });
    },

    onError: (err) => {
      console.error('useDeleteElement', err);
    },
  });
}

export function useUploadImageElement() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: Parameters<typeof getURLUploadImageElement>[0]) =>
      getURLUploadImageElement(params),
    {
      onSuccess: () => {
        // queryClient.invalidateQueries([queryKey.element]);
      },
      onError: (err) => {
        console.error('useUploadImageElement', err);
      },
    }
  );
}

export function useMutationDownloadImageElement() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: Parameters<typeof downloadImageElement>[0]) =>
      downloadImageElement(params),
    {
      onSuccess: () => {
        // queryClient.invalidateQueries([queryKey.element]);
      },
      onError: (err) => {
        console.error('useDownloadImageElement', err);
      },
    }
  );
}

export function useGetDownloadImageElement(
  param: Parameters<typeof downloadImageElement>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.downloadImageElement, sortObjectKeys(param)],
    () => downloadImageElement(param),
    { keepPreviousData: true, ...options }
  ) as UseQueryResult<Awaited<ReturnType<typeof downloadImageElement>>>;
}

export function useGetImageUrl(
  param: Parameters<typeof getImageUrl>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.element, sortObjectKeys(param)],
    () => getImageUrl(param),
    {
      keepPreviousData: true,
      ...options,
      onError: (err) => {
        console.error('useGetImageUrl', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getImageUrl>>>;
}
